import { useMemo } from 'react';
import { useQuery } from 'react-query';

import config from '../config';
import type {
  UserInfoError,
  UserInfoResponse,
} from '../stores/auth-store/types';
import type { UserInfo } from '../stores/models/user';
import {
  isSubscriptionsUrl,
  saveCheckoutReturnUrl,
} from '../utils/checkout-return-util';

import { useAccountApi } from './api';

type UserHookOptions = {
  enabled?: boolean;
  identityLoading: boolean;
  setUser: (user: UserInfo) => void;
  notFound: () => void;
};

export default function useUser({
  enabled = true,
  identityLoading,
  setUser,
  notFound,
}: UserHookOptions) {
  const api = useAccountApi();

  const { data, error, isFetched, isFetching, isLoading, refetch } = useQuery<
    UserInfoResponse,
    UserInfoError
  >(
    [config.queryKeys.auth],
    async () => {
      const data = await api.userInfo();
      return {
        ...data,
        user: {
          ...data.user,
          profiles: data.user.profiles?.sort((a, b) =>
            a.label > b.label ? 1 : -1
          ),
        },
      };
    },
    {
      enabled,
      onError: (err) => {
        switch (err.notFound) {
          case true:
            // Save checkout URL before redirecting to profile (if on subscriptions page)
            if (typeof window !== 'undefined') {
              const currentUrl = window.location.href;
              if (
                isSubscriptionsUrl(currentUrl) &&
                currentUrl.includes('=true')
              ) {
                saveCheckoutReturnUrl(currentUrl);
              }
            }
            notFound();
            break;

          case false:
          case undefined:
            return;
          default:
            return;
        }
      },
      onSuccess: (data) => {
        setUser(data.user);
      },
    }
  );

  const user = useMemo(() => data?.user, [data?.user]);

  const { profileNames, profileRecords } = useMemo(() => {
    const profileNames: Record<string, string> = {};
    const profileRecords: Record<string, EntityReference> = {};
    const totalPermissions = user?.permissions?.length;
    if (!totalPermissions) {
      return { profileNames, profileRecords };
    }

    for (let i = 0; i < totalPermissions; i++) {
      const permission = user.permissions[i];
      if (!permission.entityName) {
        continue;
      }

      const ref: EntityReference = {
        id: permission.entityId,
        name: permission.entityName,
        nameId: permission.entityNameId,
      };

      if (permission.entityId) {
        profileNames[permission.entityId] = ref.name;
        profileRecords[permission.entityId] = ref;
      }

      if (permission.entityNameId) {
        profileNames[permission.entityId] = ref.name;
        profileRecords[permission.entityNameId] = ref;
      }
    }

    return { profileNames, profileRecords };
  }, [user?.permissions]);

  return {
    error,
    identityLoading,
    isFetched,
    isFetching,
    isLoading,
    isLoggedIn: user?.isAuthenticated || false,
    profileCount: useMemo(
      () => user?.profiles.length || 0,
      [user?.profiles.length]
    ),
    profileNames,
    profileRecords,
    profiles: user?.profiles,
    refetch,
    user,
  };
}
